import React from 'react';
import { TemplateProps } from '../../../types/cv';
import { Mail, Phone, MapPin } from 'lucide-react';
import { CV_LABELS, frPresent } from '../../../utils/cvLabels';
import { isSectionContinuation } from '../../../utils/cvPagination';
import SectionHeading from '../SectionHeading';

const OceanWaveTemplate: React.FC<TemplateProps> = ({ data, isContinuationPage = false, showPlaceholders = false, pageIndex = 0, fullData }) => {
    const primaryColor = data.style?.primaryColor || '#00A8E8';
    const { fontFamily = 'font-sans', textColor = '#1e293b', backgroundColor = '#ffffff', sectionBackgroundColor = '#ffffff', imageBorderColor = '#00D4FF' } = data.style || {};

    const source = fullData || data;
    const eduContinued = isSectionContinuation(source, 'education', pageIndex);
    const expContinued = isSectionContinuation(source, 'experience', pageIndex);
    const skillsContinued = isSectionContinuation(source, 'skills', pageIndex);
    const langContinued = isSectionContinuation(source, 'languages', pageIndex);

    const phone = data.personalInfo.phone || (showPlaceholders ? '+01234 567 890' : '');
    const email = data.personalInfo.email || (showPlaceholders ? 'email@exemple.com' : '');
    const address = data.personalInfo.address || (showPlaceholders ? 'Adresse' : '');
    const summary = data.personalInfo.summary || (showPlaceholders
        ? 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed diam nonummy nibh euismod tincidunt ut laoreet dolore magna aliquam erat volutpat. Lorem ipsum dolor sit amet, consectetur adipiscing elit, ad minim veniam, quis nostrud exerci tation ullamcorper suscipit lobortis nisl ut aliquip ex ea commodo consequat.'
        : '');
    const photoSrc = data.personalInfo.profileImage || (showPlaceholders
        ? 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?q=80&w=1976&auto=format&fit=crop'
        : '');

    return (
        <div className={`shadow-2xl cv-page-sheet h-[297mm] max-h-[297mm] w-full flex overflow-hidden ${fontFamily} relative`} style={{ backgroundColor: backgroundColor, color: textColor }}>

            {/* Background Decorations - Ocean Wave Style */}
            <div className="absolute top-[-80px] left-[-80px] w-80 h-80 bg-cyan-50 rounded-full opacity-40 z-0"></div>
            <div className="absolute top-10 right-[-40px] w-48 h-48 bg-cyan-100 rounded-full opacity-30 z-0"></div>
            <div className="absolute bottom-[-60px] left-[30%] w-40 h-40 bg-cyan-50 rounded-full opacity-25 z-0"></div>

            {/* Diagonal Wave Accent */}
            <div className="absolute top-0 left-0 w-[45%] h-full bg-gradient-to-br from-[#1E3A5F] via-[#2B5A8E] to-[#00A8E8] opacity-95 z-0" style={{ clipPath: 'polygon(0 0, 100% 0, 75% 100%, 0% 100%)' }}></div>
            <div className="absolute top-0 left-[30%] w-[20%] h-full bg-gradient-to-br from-[#00A8E8] to-[#00D4FF] opacity-80 z-0" style={{ clipPath: 'polygon(0 0, 100% 0, 60% 100%, 0% 100%)' }}></div>

            {/* Left Sidebar */}
            <div data-cv-sidebar className="w-1/3 text-white p-8 flex flex-col z-10 relative overflow-hidden">
                {isContinuationPage ? (
                    <div className="mt-4">
                        <p className="cv-fs-name text-xs font-bold uppercase tracking-widest text-white/80">
                            {data.personalInfo.firstName} {data.personalInfo.lastName}
                        </p>
                        <p className="text-[10px] uppercase tracking-wider text-white/40 mt-2">{CV_LABELS.suite}</p>
                    </div>
                ) : (
                    <>
                {(photoSrc || showPlaceholders) && (
                <div className="mb-5 flex flex-col items-center">
                    <div className="w-36 h-36 rounded-full border-[6px] overflow-hidden mb-4 shadow-2xl relative" style={{ borderColor: imageBorderColor }}>
                        {photoSrc ? (
                            <img src={photoSrc} alt="Profile" className="w-full h-full object-cover" />
                        ) : null}
                        <div className="absolute -bottom-3 -right-3 w-10 h-10 rounded-full border-4 border-white shadow-lg" style={{ backgroundColor: primaryColor }}></div>
                    </div>
                    <div className="px-5 py-1.5 rounded-full text-[9px] font-black uppercase tracking-[0.15em] shadow-lg" style={{ backgroundColor: primaryColor }}>
                        {CV_LABELS.publicProfile}
                    </div>
                </div>
                )}

                {(phone || email || address || showPlaceholders) && (
                <section className="mb-5">
                    <h2 className="text-sm font-black uppercase tracking-[0.25em] mb-6 flex items-center gap-2">
                        {CV_LABELS.contact} <div className="h-px flex-1 bg-white/20"></div>
                    </h2>
                    <div className="cv-fs-contact space-y-4">
                        {phone && (
                            <div className="flex items-center gap-2">
                                <Phone className="w-3.5 h-3.5" />
                                <span className="text-[11px] font-medium break-all">{phone}</span>
                            </div>
                        )}
                        {email && (
                            <div className="flex items-center gap-2">
                                <Mail className="w-3.5 h-3.5" />
                                <span className="text-[11px] font-medium break-all">{email}</span>
                            </div>
                        )}
                        {address && (
                            <div className="flex items-center gap-2">
                                <MapPin className="w-3.5 h-3.5" />
                                <span className="text-[11px] font-medium break-words">{address}</span>
                            </div>
                        )}
                    </div>
                </section>
                )}

                {summary && (
                <section className="mb-5">
                    <h2 className="text-sm font-black uppercase tracking-[0.25em] mb-6 flex items-center gap-2">
                        {CV_LABELS.about} <div className="h-px flex-1 bg-white/20"></div>
                    </h2>
                    <p className="cv-fs-summary text-[11px] leading-relaxed text-cyan-50 font-medium">
                        {summary}
                    </p>
                </section>
                )}

                {((data.skills && data.skills.length > 0) || showPlaceholders) && (
                <section className="mb-5">
                    <SectionHeading title={CV_LABELS.skills} isContinuation={skillsContinued} primaryColor="#ffffff" variant="plain" />
                    <div className="cv-fs-skills grid grid-cols-3 gap-2">
                        {(data.skills && data.skills.length > 0 ? data.skills.map(s => ({ id: s.id, name: s.name, val: s.level || 50 })) : showPlaceholders ? [
                            { name: 'Design', val: 95 },
                            { name: 'SEO', val: 90 },
                            { name: 'Analyse', val: 80 }
                        ] : []).map((skill, i) => (
                            <div
                                key={'id' in skill ? skill.id || skill.name || i : skill.name || i}
                                className="flex flex-col items-center min-w-0"
                                data-cv-block
                                data-cv-item-id={'id' in skill ? skill.id || undefined : undefined}
                            >
                                <div className="relative flex items-center justify-center mb-1" style={{ width: 36, height: 36 }}>
                                    <svg width={36} height={36} className="transform -rotate-90">
                                        <circle cx="18" cy="18" r="14" stroke="rgba(255,255,255,0.15)" strokeWidth="3" fill="transparent" />
                                        <circle
                                            cx="18"
                                            cy="18"
                                            r="14"
                                            stroke="#00D4FF"
                                            strokeWidth="3"
                                            fill="transparent"
                                            strokeDasharray={2 * Math.PI * 14}
                                            strokeDashoffset={2 * Math.PI * 14 * (1 - skill.val / 100)}
                                            strokeLinecap="round"
                                        />
                                    </svg>
                                    <span className="absolute text-[7px] font-black text-[#00D4FF]">{Math.round(skill.val)}%</span>
                                </div>
                                <span className="text-[8px] font-bold uppercase tracking-wide text-center leading-tight break-words w-full">{skill.name}</span>
                            </div>
                        ))}
                    </div>
                </section>
                )}

                {((data.references && data.references.length > 0) || showPlaceholders) && (
                <section>
                    <h2 className="text-sm font-black uppercase tracking-[0.25em] mb-6 flex items-center gap-2">
                        {CV_LABELS.references} <div className="h-px flex-1 bg-white/20"></div>
                    </h2>
                    <div className="cv-fs-references space-y-4">
                        {data.references && data.references.length > 0 ? data.references.slice(0, 1).map((ref, i) => (
                            <div
                                key={ref.id || i}
                                data-cv-block
                                data-cv-item-id={ref.id || undefined}
                            >
                                <p className="text-sm font-black">{ref.name}</p>
                                <p className="text-[9px] text-cyan-100 font-bold">{ref.position}</p>
                                <p className="text-[9px] text-cyan-200 mt-1">Phone: {ref.phone}</p>
                                <p className="text-[9px] text-cyan-200">Email: {ref.email}</p>
                            </div>
                        )) : showPlaceholders ? (
                            <div>
                                <p className="text-sm font-black">Mark Johnson</p>
                                <p className="text-[9px] text-cyan-100 font-bold">Directeur — Entreprise</p>
                                <p className="text-[9px] text-cyan-200 mt-1">Phone: +000 1234 6789</p>
                                <p className="text-[9px] text-cyan-200">Email: mail@here</p>
                            </div>
                        ) : null}
                    </div>
                </section>
                )}
                    </>
                )}
            </div>

            {/* Right Content */}
            <div data-cv-main className="flex-1 min-h-0 overflow-hidden p-12 flex flex-col z-10" style={{ backgroundColor: backgroundColor }}>

                {isContinuationPage ? (
                    <div className="mb-4 pb-2 border-b border-slate-200 shrink-0">
                        <p className="text-sm font-bold uppercase tracking-widest" style={{ color: textColor, opacity: 0.5 }}>{CV_LABELS.suite}</p>
                    </div>
                ) : (
                <header className="mb-6">
                    <h1 className="cv-fs-name text-6xl font-black tracking-tighter uppercase leading-none mb-2">
                        <span style={{ color: '#' === textColor[0] ? textColor : '#0f172a' }}>{data.personalInfo.firstName || (showPlaceholders ? 'NAME' : '')}</span><br />
                        <span style={{ color: primaryColor }}>{data.personalInfo.lastName || (showPlaceholders ? 'SURNAME' : '')}</span>
                    </h1>
                    <p className="cv-fs-profession text-lg font-bold uppercase tracking-[0.3em] mt-2" style={{ color: textColor, opacity: 0.6 }}>
                        {data.personalInfo.profession || (showPlaceholders ? 'DIGITAL MARKETER' : '')}
                    </p>
                </header>
                )}

                {isContinuationPage && data.skills && data.skills.length > 0 && (
                    <section className="mb-6">
                        <SectionHeading title={CV_LABELS.skills} isContinuation={skillsContinued} primaryColor={primaryColor} variant="plain" />
                        <div className="cv-fs-skills grid grid-cols-3 gap-4">
                            {data.skills.map((skill, i) => (
                                <div
                                    key={skill.id || skill.name || i}
                                    className="flex items-center gap-2"
                                    data-cv-block
                                    data-cv-item-id={skill.id || undefined}
                                >
                                    <div className="w-8 h-8 rounded-full border-2 flex items-center justify-center text-[8px] font-bold shrink-0" style={{ borderColor: primaryColor, color: primaryColor }}>
                                        {Math.round(skill.level || 50)}%
                                    </div>
                                    <span className="text-xs font-bold break-words" style={{ color: textColor }}>{skill.name}</span>
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {(data.experience.length > 0 || showPlaceholders) && (
                <section className="mb-6">
                    <SectionHeading title={CV_LABELS.experience} isContinuation={expContinued} primaryColor={primaryColor} variant="plain" />
                    <div className="cv-fs-experience space-y-5">
                        {data.experience.length > 0 ? data.experience.map((exp, i) => (
                            <div
                                key={exp.id || i}
                                data-cv-block
                                data-cv-item-id={exp.id || undefined}
                            >
                                <p className="text-xs font-bold mb-1" style={{ color: textColor, opacity: 0.5 }}>{exp.startDate} - {frPresent(exp.isCurrent, exp.endDate)}</p>
                                <h3 className="text-sm font-black uppercase" style={{ color: textColor }}>{exp.position}</h3>
                                <p className="text-xs font-bold mb-2" style={{ color: primaryColor }}>{exp.company}</p>
                                <p className="text-[11px] leading-relaxed" style={{ color: textColor, opacity: 0.7 }}>
                                    {exp.description}
                                </p>
                            </div>
                        )) : showPlaceholders ? (
                            <>
                                <div>
                                    <p className="text-xs font-bold text-slate-400 mb-1">2020 - 2021</p>
                                    <h3 className="text-sm font-black text-slate-900 uppercase">Poste</h3>
                                    <p className="text-xs font-bold mb-2" style={{ color: primaryColor }}>Entreprise</p>
                                    <p className="text-[11px] text-slate-600 leading-relaxed">
                                        Décrivez vos missions et réalisations.
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs font-bold text-slate-400 mb-1">2018 - 2020</p>
                                    <h3 className="text-sm font-black text-slate-900 uppercase">Poste</h3>
                                    <p className="text-xs font-bold mb-2" style={{ color: primaryColor }}>Entreprise</p>
                                    <p className="text-[11px] text-slate-600 leading-relaxed">
                                        Décrivez vos missions et réalisations.
                                    </p>
                                </div>
                            </>
                        ) : null}
                    </div>
                </section>
                )}

                {(data.education.length > 0 || showPlaceholders) && (
                <section className="mb-6">
                    <SectionHeading title={CV_LABELS.education} isContinuation={eduContinued} primaryColor={primaryColor} variant="plain" />
                    <div className="cv-fs-education space-y-4">
                        {data.education.length > 0 ? data.education.map((edu, i) => (
                            <div
                                key={edu.id || i}
                                data-cv-block
                                data-cv-item-id={edu.id || undefined}
                            >
                                <p className="text-xs font-bold" style={{ color: textColor, opacity: 0.5 }}>{edu.startDate} - {frPresent(edu.isCurrent, edu.endDate)}</p>
                                <h3 className="text-sm font-black uppercase" style={{ color: textColor }}>{edu.degree} - {edu.field}</h3>
                                <p className="text-xs font-bold" style={{ color: primaryColor }}>{edu.institution}</p>
                                {edu.description && <p className="text-[11px] mt-1" style={{ color: textColor, opacity: 0.7 }}>{edu.description}</p>}
                            </div>
                        )) : showPlaceholders ? (
                            <>
                                <div>
                                    <p className="text-xs font-bold text-slate-400">2011 - 2014</p>
                                    <h3 className="text-sm font-black text-slate-900 uppercase">Diplôme</h3>
                                    <p className="text-xs font-bold" style={{ color: primaryColor }}>Établissement</p>
                                </div>
                                <div>
                                    <p className="text-xs font-bold text-slate-400">2007 - 2010</p>
                                    <h3 className="text-sm font-black text-slate-900 uppercase">Diplôme</h3>
                                    <p className="text-xs font-bold" style={{ color: primaryColor }}>Établissement</p>
                                </div>
                            </>
                        ) : null}
                    </div>
                </section>
                )}

                {((data.interests && data.interests.length > 0) || showPlaceholders) && (
                <section>
                    <h2 className="text-xl font-black uppercase tracking-[0.15em] mb-4 flex items-center gap-3" style={{ color: primaryColor }}>
                        {CV_LABELS.interests} <div className="h-1 flex-1 bg-cyan-50 rounded-full"></div>
                    </h2>
                    <div className="cv-fs-interests grid grid-cols-3 gap-y-2">
                        {(data.interests && data.interests.length > 0 ? data.interests : showPlaceholders ? [
                            { name: 'Natation' }, { name: 'Vélo' }, { name: 'Voyage' }, { name: 'Échecs' },
                            { name: 'Musique' }, { name: 'Cinéma' }, { name: 'Lecture' }, { name: 'Vidéos' }
                        ] : []).map((interest, i) => (
                            <div
                                key={interest.id || interest.name || i}
                                className="flex items-center gap-2"
                                data-cv-block
                                data-cv-item-id={interest.id || undefined}
                            >
                                <div className="w-4 h-4 rounded-md border-2 border-cyan-100 flex items-center justify-center">
                                    <div className="w-2 h-2 rounded-sm" style={{ backgroundColor: primaryColor }}></div>
                                </div>
                                <span className="text-xs font-bold" style={{ color: textColor }}>{interest.name}</span>
                            </div>
                        ))}
                    </div>
                </section>
                )}
            </div>
        </div>
    );
};

export default OceanWaveTemplate;
